import React, { useState, useEffect } from "react";
import { Container, Typography, TextField, Button, Grid, Paper, MenuItem, Snackbar, Alert } from "@mui/material";
import axios from "axios";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';

const defaultSettings = {
  companyName: "",
  workStart: "09:00",
  workEnd: "18:00",
  leaveLimit: 14,
  currency: "TRY",
  overtimeRate: '',
  smtp_server: 'bytebridge.com.tr',
  smtp_port: 465,
  smtp_username: 'info@bytebridge.com.tr',
  smtp_password: 'GURkan5391',
  smtp_use_tls: false,
};

export default function CompanySettings() {
  const [settings, setSettings] = useState({
    ...defaultSettings,
    // Ensure overtimeRate is always a string to prevent controlled input warning
    overtimeRate: defaultSettings.overtimeRate || ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    axios.get("https://pdks.bytebridge.com.tr/api/company-settings")
      .then(res => {
        if (res.data) {
          // Ensure all required fields are properly initialized
          const data = {
            ...res.data,
            // Convert values to appropriate types
            overtimeRate: res.data.overtimeRate != null ? String(res.data.overtimeRate) : '',
            smtp_port: res.data.smtp_port || 465,
            smtp_use_tls: res.data.smtp_use_tls === true || res.data.smtp_use_tls === 'true',
            reports_enabled: res.data.reports_enabled || 'false',
            operations_enabled: res.data.operations_enabled || 'false',
            reports_access_roles: res.data.reports_access_roles || 'admin,manager',
            operations_access_roles: res.data.operations_access_roles || 'admin,manager,operator',
            // Ensure SMTP password is included (use empty string if not set)
            smtp_password: res.data.smtp_password || ''
          };
          setSettings(data);
        }
      })
      .catch((error) => {
        console.error('Error fetching settings:', error);
        setError("Ayarlar yüklenirken bir hata oluştu.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Ensure numeric fields are handled correctly
    const newValue = type === 'checkbox' 
      ? checked 
      : (name === 'overtimeRate' ? String(value) : value);
    setSettings((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Prepare the data to be sent to the server
    const dataToSend = {
      ...settings,
      // Ensure all required fields are included
      reports_enabled: settings.reports_enabled || 'false',
      operations_enabled: settings.operations_enabled || 'false',
      reports_access_roles: settings.reports_access_roles || 'admin,manager',
      operations_access_roles: settings.operations_access_roles || 'admin,manager,operator',
      // Ensure SMTP password is included (use empty string if not set)
      smtp_password: settings.smtp_password || ''
    };
    
    console.log('Sending data:', dataToSend);
    
    axios.post("https://pdks.bytebridge.com.tr/api/company-settings", dataToSend)
      .then((response) => {
        console.log('Response:', response.data);
        setSuccess(true);
        setError("");
      })
      .catch((error) => {
        console.error('Error:', error);
        const errorMessage = error.response?.data?.error || "Ayarlar kaydedilemedi!";
        setError(errorMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Container maxWidth="sm" style={{ marginTop: 40 }}>
      <Paper elevation={3} style={{ padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <Tooltip title="Şirket ayarlarını bu sayfadan yönetebilirsiniz.">
            <InfoOutlinedIcon color="primary" style={{ marginRight: 8, cursor: 'pointer' }} />
          </Tooltip>
          <Typography variant="h5" gutterBottom style={{ margin: 0 }}>
            Şirket Ayarları
          </Typography>
        </div>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Şirket Adı"
                name="companyName"
                value={settings.companyName}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Çalışma Başlangıcı"
                name="workStart"
                type="time"
                value={settings.workStart}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Çalışma Bitişi"
                name="workEnd"
                type="time"
                value={settings.workEnd}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Yıllık İzin Limiti (gün)"
                name="leaveLimit"
                type="number"
                value={settings.leaveLimit}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Para Birimi"
                name="currency"
                value={settings.currency}
                onChange={handleChange}
                select
                fullWidth
              >
                <MenuItem value="TRY">TRY</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="EUR">EUR</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Fazla Mesai Oranı"
                name="overtimeRate"
                value={settings.overtimeRate}
                onChange={handleChange}
                fullWidth
                helperText="Saatlik fazla mesai oranı (örn: 1.5)"
              />
            </Grid>
            {/* SMTP Settings */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" style={{ marginTop: 20, marginBottom: 6 }}>
                SMTP (Mail) Ayarları
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="SMTP Sunucu"
                name="smtp_server"
                value={settings.smtp_server}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="SMTP Port"
                name="smtp_port"
                type="number"
                value={settings.smtp_port}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Kullanıcı Adı"
                name="smtp_username"
                value={settings.smtp_username}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Şifre"
                name="smtp_password"
                type="password"
                value={settings.smtp_password}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} style={{ display: 'flex', alignItems: 'center' }}>
              <label style={{ marginRight: 8 }}>TLS Kullan</label>
              <input
                type="checkbox"
                name="smtp_use_tls"
                checked={!!settings.smtp_use_tls}
                onChange={handleChange}
                style={{ width: 20, height: 20 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button type="submit" variant="contained" color="primary" fullWidth disabled={loading}>
                {loading ? "Kaydediliyor..." : "Kaydet"}
              </Button>
            </Grid>
          </Grid>
        </form>
        <Snackbar open={success} autoHideDuration={3000} onClose={() => setSuccess(false)}>
          <Alert onClose={() => setSuccess(false)} severity="success" sx={{ width: '100%' }}>
            Ayarlar kaydedildi!
          </Alert>
        </Snackbar>
        <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError("")}>
          <Alert onClose={() => setError("")} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
      </Paper>
    </Container>
  );
}
